// # Mistake Counter Component for Step 1
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import './counter1.css';
import { useAuthStore } from '../../../auth/state';
import { useState, useEffect, useMemo } from 'react';
// # Import Firebase for user lookup
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';

// # Import role access utility
import m1Access from '../m1_access';
import cacheManager from '../../../auth/cache_manager';

// # Import step1 icons from my_notes.js structure
import anatomyIcon from '../../../general/library/step1/step1_icons/anatomy.png';
import biochemistryIcon from '../../../general/library/step1/step1_icons/biochemistry.png';
import bloodIcon from '../../../general/library/step1/step1_icons/blood.png';
import cnsIcon from '../../../general/library/step1/step1_icons/cns.png';
import endocrineIcon from '../../../general/library/step1/step1_icons/endocrine.png';
import eyeIcon from '../../../general/library/step1/step1_icons/eye.png';
import gitIcon from '../../../general/library/step1/step1_icons/git.png';
import heartIcon from '../../../general/library/step1/step1_icons/heart.png';
import immunologyIcon from '../../../general/library/step1/step1_icons/immunology.png';
import infectiousIcon from '../../../general/library/step1/step1_icons/infectious.png';
import kidneyIcon from '../../../general/library/step1/step1_icons/kidney.png';
import lungsIcon from '../../../general/library/step1/step1_icons/lungs.png';
import musculoskeletalIcon from '../../../general/library/step1/step1_icons/musculoskeletal.png';
import pathologyIcon from '../../../general/library/step1/step1_icons/pathology.png';
import pharmaIcon from '../../../general/library/step1/step1_icons/pharma.png';
import psychiatryIcon from '../../../general/library/step1/step1_icons/psychiatry.png';
import publicHealthIcon from '../../../general/library/step1/step1_icons/public_health.png';
import reproductiveIcon from '../../../general/library/step1/step1_icons/reproductive-system.png';
import skinIcon from '../../../general/library/step1/step1_icons/skin.png';
import revisionIcon from '../../../general/library/step1/step1_icons/revision.png';

function Counter1({ isAdmin, isTutor, isMentorView }) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { username } = useParams(); // # Get username from URL params if in mentor view
  const [targetUserId, setTargetUserId] = useState(null);
  const [isMentor, setIsMentor] = useState(false);
  const [menteeId, setMenteeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Create base path based on user role
  const basePath = useMemo(() => {
    return m1Access.getBasePath({
      isAdmin, 
      isTutor, 
      isMentorView,
      username
    });
  }, [isAdmin, isTutor, isMentorView, username]);

  // # Get mentee ID from username when in mentor view
  useEffect(() => {
    const fetchMenteeByUsername = async () => {
      if (isMentorView && username) {
        try {
          setLoading(true);
          // # Query users collection for the username
          const usersCollection = collection(db, 'users');
          const q = query(usersCollection, where('username', '==', username));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const userData = querySnapshot.docs[0].data();
            const userId = querySnapshot.docs[0].id;
            setMenteeId(userId);
            // # Store mentee ID in sessionStorage for other components
            sessionStorage.setItem('currentViewingMenteeId', userId);
            sessionStorage.setItem('currentViewingMenteeUsername', username);
          } else {
            console.error('No user found with username:', username);
            setError('User not found');
          }
        } catch (error) {
          console.error('Error fetching mentee by username:', error);
          setError('Error loading user data');
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    
    fetchMenteeByUsername();
  }, [isMentorView, username]);

  // # Get the correct user ID based on role context
  useEffect(() => {
    // # Use the access utility to determine the target user ID
    const menteeIdToUse = menteeId || sessionStorage.getItem('currentViewingMenteeId');
    const userId = m1Access.getTargetUserId({
      isAdmin,
      isTutor,
      viewingMenteeId: menteeIdToUse,
      currentUserId: user?.uid
    });
    
    setTargetUserId(userId);
    setMenteeId(menteeIdToUse || user?.uid);
    setIsMentor(userId !== user?.uid);
  }, [user?.uid, isAdmin, isTutor, menteeId]);

  // # Define chapters based on my_notes.js structure
  const chapters = [
    { id: 'Biochemistry', title: 'Biochemistry', icon: biochemistryIcon },
    { id: 'Immunology', title: 'Immunology', icon: immunologyIcon },
    { id: 'Microbiology', title: 'Microbiology', icon: infectiousIcon },
    { id: 'General Pathology', title: 'General Pathology', icon: pathologyIcon },
    { id: 'Pharmacology', title: 'Pharmacology', icon: pharmaIcon },
    { id: 'Public Health', title: 'Public Health Sciences', icon: publicHealthIcon },
    { id: 'Anatomy', title: 'Anatomy & Histology', icon: anatomyIcon },
    { id: 'Cardiovascular', title: 'Cardiovascular System', icon: heartIcon },
    { id: 'Endocrine', title: 'Endocrine System', icon: endocrineIcon },
    { id: 'Gastrointestinal', title: 'Gastrointestinal System', icon: gitIcon },
    { id: 'Hematology', title: 'Hematology & Oncology', icon: bloodIcon },
    { id: 'Musculoskeletal', title: 'Musculoskeletal & Connective Tissue', icon: musculoskeletalIcon },
    { id: 'Dermatology', title: 'Dermatology', icon: skinIcon },
    { id: 'Neurology', title: 'Neurology', icon: cnsIcon },
    { id: 'Special Senses', title: 'Special Senses', icon: eyeIcon },
    { id: 'Psychiatry', title: 'Psychiatry', icon: psychiatryIcon },
    { id: 'Renal', title: 'Renal System', icon: kidneyIcon },
    { id: 'Reproductive', title: 'Reproductive System', icon: reproductiveIcon },
    { id: 'Respiratory', title: 'Respiratory System', icon: lungsIcon },
    { id: 'Rapid Review', title: 'Rapid Review Series', icon: revisionIcon }
  ];

  // # Handle back navigation
  const handleBack = () => {
    // # Use appropriate back path based on role
    if (isMentorView && username) {
      navigate(`/mentor/step1/${username}`);
    } else if (isAdmin) {
      navigate(`/admin/mentorship/step1`);
    } else if (isTutor) {
      navigate(`/tutor/mentorship/step1`);
    } else {
      navigate(`/mentorship/step1`);
    }
  };

  // # Handle chapter click
  const handleChapterClick = (chapterId) => {
    // # Create appropriate navigation path based on role
    if (isMentorView && username) {
      navigate(`/mentor/step1/${username}/mentoring1/counter1/${chapterId}`);
    } else {
      navigate(`${basePath}/step1/mentoring1/counter1/${chapterId}`);
    }
  };

  if (loading) {
    return (
      <div className={`counter1_container ${isDarkMode ? 'counter1_dark_mode' : ''}`}>
        <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
        <div className="counter1_content">
          <div className="counter1_loading">Loading mistake counter data...</div>
        </div>
        <DashboardFooter isDarkMode={isDarkMode} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`counter1_container ${isDarkMode ? 'counter1_dark_mode' : ''}`}>
        <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
        <div className="counter1_content">
          <div className="counter1_error">{error}</div>
          <button className="counter1_back_button" onClick={handleBack}>
            &larr; Back to Dashboard
          </button>
        </div>
        <DashboardFooter isDarkMode={isDarkMode} />
      </div>
    );
  }

  return (
    <div className={`counter1_container ${isDarkMode ? 'counter1_dark_mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      <div className="counter1_content">
        <div className="counter1_header">
          <button className="counter1_back_button" onClick={handleBack}>
            &larr; Back to Dashboard
          </button>
          <h1>USMLE Step 1 Chapters</h1>
          {isMentor && <span className="counter1_mentor_pill">Mentor</span>}
          {username && isMentor && (
            <div className="counter1_mentee_info">
              Viewing mistakes for: @{username}
            </div>
          )}
        </div>

        <div className="counter1_chapters_grid">
          {chapters.map(chapter => (
            <div 
              key={chapter.id} 
              className="counter1_chapter_card"
              onClick={() => handleChapterClick(chapter.id)}
            >
              <div className="counter1_chapter_header">
                <img src={chapter.icon} alt={chapter.title} className="counter1_chapter_icon" />
                <h3>{chapter.title}</h3>
              </div>
            </div>
          ))}
        </div>
      </div>
      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default Counter1;
